import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Spinner, Text,
  Card, Headline, Button, Switch, Snackbar,
} from '@vkontakte/vkui';
import { Icon24Done, Icon28Notification } from '@vkontakte/icons';
import { api } from '../../api/client';
import type { Notification, NotificationSettings } from '../../types';
import { useAuthStore } from '../../stores/auth';
import { useWeddingStore } from '../../stores/wedding';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { currentWedding, selectWedding } = useWeddingStore();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState<string | null>(null);
  const [settings, setSettings] = useState<NotificationSettings>({
    notify_tasks: user?.notify_tasks ?? true,
    notify_payments: user?.notify_payments ?? true,
    notify_messages: user?.notify_messages ?? true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.get<Notification[]>('notifications');
      setItems(list);
    } catch {
      setSnack('Не удалось загрузить уведомления');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function markRead(id: string) {
    try {
      await api.patch(`notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch {
      setSnack('Не удалось отметить');
    }
  }

  async function openNotification(n: Notification) {
    if (!n.is_read) await markRead(n.id);
    if (currentWedding?.id !== n.wedding_id) {
      try {
        await selectWedding(n.wedding_id);
      } catch {
        /* откроем то, что есть */
      }
    }
    if (n.task_id) {
      navigate(`/tasks/${n.task_id}`);
    } else if (n.payment_id) {
      navigate('/budget');
    } else {
      navigate('/messages');
    }
  }

  async function markAll() {
    try {
      await api.post('notifications/read-all');
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {
      setSnack('Не удалось отметить');
    }
  }

  async function toggle(key: keyof NotificationSettings, value: boolean) {
    const next = { ...settings, [key]: value };
    setSettings(next);
    try {
      const updated = await api.put<NotificationSettings>('notifications/settings', { [key]: value });
      useAuthStore.setState((s) => (s.user ? { user: { ...s.user, ...updated } } : {}));
    } catch {
      setSettings(settings);
      setSnack('Не удалось сохранить настройки');
    }
  }

  const unread = items.filter((n) => !n.is_read).length;

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Уведомления
      </PanelHeader>

      <Group header={<Text>Настройки</Text>}>
        <Card style={{ margin: 12, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text>Задачи</Text>
            <Switch checked={settings.notify_tasks} onChange={(e) => void toggle('notify_tasks', e.target.checked)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text>Платежи</Text>
            <Switch checked={settings.notify_payments} onChange={(e) => void toggle('notify_payments', e.target.checked)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text>Сообщения</Text>
            <Switch checked={settings.notify_messages} onChange={(e) => void toggle('notify_messages', e.target.checked)} />
          </div>
        </Card>
      </Group>

      <Group header={<Text>Список{unread ? ` · непрочитанных: ${unread}` : ''}</Text>}>
        {loading && (
          <Div style={{ display: 'flex', justifyContent: 'center' }}>
            <Spinner size="m" />
          </Div>
        )}
        {!loading && items.length === 0 && (
          <Div style={{ textAlign: 'center', padding: 24 }}>
            <Icon28Notification style={{ opacity: 0.4, marginBottom: 8 }} />
            <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Пока нет уведомлений</Text>
          </Div>
        )}
        {!loading && items.length > 0 && (
          <Div>
            <Button size="m" mode="secondary" stretched style={{ marginBottom: 12 }} onClick={() => void markAll()}>
              Прочитать все
            </Button>
            {items.map((n) => (
              <Card
                key={n.id}
                onClick={() => void openNotification(n)}
                style={{
                  marginBottom: 8,
                  padding: 12,
                  cursor: 'pointer',
                  opacity: n.is_read ? 0.55 : 1,
                  borderLeft: n.is_read ? '3px solid transparent' : '3px solid var(--vkui--color_background_accent)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Headline level="2">{n.title || 'Уведомление'}</Headline>
                    {n.body && (
                      <Text style={{ marginTop: 4, color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
                        {n.body}
                      </Text>
                    )}
                    <Text style={{ marginTop: 4, fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                      {new Date(n.scheduled_at).toLocaleString('ru-RU')}
                    </Text>
                  </div>
                  {!n.is_read && (
                    <Button
                      size="s"
                      mode="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        void markRead(n.id);
                      }}
                    >
                      <Icon24Done />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </Div>
        )}
      </Group>

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)}>{snack}</Snackbar>
      )}
    </Panel>
  );
}
